import { FileTransferConfig, FileTransferMetadata, FileTransferPolicyError } from "@istari-frontend/resource/model";

export const DEFAULT_FILE_TRANSFER_CONFIG: FileTransferConfig = Object.freeze({
  uploadConcurrency: 2,
  contentConcurrency: 2,
  previewMaxBytes: 50_000_000,
  diffMaxBytes: 100_000_000,
  downloadMaxBytes: 100_000_000,
  zipMaxBytes: 100_000_000,
  zipMaxFiles: 100,
});

export function parseFileTransferConfig(
  values: Partial<Record<keyof FileTransferConfig, string | undefined>>,
): FileTransferConfig {
  return {
    uploadConcurrency: parsePositiveInteger(values.uploadConcurrency, DEFAULT_FILE_TRANSFER_CONFIG.uploadConcurrency),
    contentConcurrency: parsePositiveInteger(
      values.contentConcurrency,
      DEFAULT_FILE_TRANSFER_CONFIG.contentConcurrency,
    ),
    previewMaxBytes: parsePositiveInteger(values.previewMaxBytes, DEFAULT_FILE_TRANSFER_CONFIG.previewMaxBytes),
    diffMaxBytes: parsePositiveInteger(values.diffMaxBytes, DEFAULT_FILE_TRANSFER_CONFIG.diffMaxBytes),
    downloadMaxBytes: parsePositiveInteger(values.downloadMaxBytes, DEFAULT_FILE_TRANSFER_CONFIG.downloadMaxBytes),
    zipMaxBytes: parsePositiveInteger(values.zipMaxBytes, DEFAULT_FILE_TRANSFER_CONFIG.zipMaxBytes),
    zipMaxFiles: parsePositiveInteger(values.zipMaxFiles, DEFAULT_FILE_TRANSFER_CONFIG.zipMaxFiles),
  };
}

export function assertFileAdmission(file: FileTransferMetadata, limit: number): void {
  if (!Number.isSafeInteger(file.size) || file.size < 0) {
    throw new FileTransferPolicyError(
      "invalid-size",
      `The exact revision size of "${file.name}" is unavailable or invalid. Refresh its metadata before trying again.`,
    );
  }
  if (file.size > limit) {
    throw new FileTransferPolicyError(
      "file-too-large",
      `"${file.name}" (${file.size} bytes) exceeds the ${limit}-byte buffered file limit.`,
    );
  }
}

export function assertComparisonAdmission(
  files: readonly FileTransferMetadata[],
  config: Pick<FileTransferConfig, "diffMaxBytes">,
): void {
  assertAggregateAdmission(files, config.diffMaxBytes, "comparison-too-large", "Comparison");
}

export function assertZipAdmission(
  files: readonly FileTransferMetadata[],
  config: Pick<FileTransferConfig, "zipMaxBytes" | "zipMaxFiles">,
): void {
  if (files.length > config.zipMaxFiles) {
    throw new FileTransferPolicyError(
      "zip-too-many-files",
      `ZIP downloads are limited to ${config.zipMaxFiles} files.`,
    );
  }
  assertAggregateAdmission(files, config.zipMaxBytes, "zip-too-large", "ZIP download");
}

export function validateContentLength(
  content: ArrayBuffer | ArrayBufferView,
  file: FileTransferMetadata,
  limit: number,
): void {
  assertFileAdmission(file, limit);
  assertFileAdmission({ name: file.name, size: content.byteLength }, limit);
  if (content.byteLength !== file.size) {
    throw new FileTransferPolicyError(
      "content-length-mismatch",
      `The content length of "${file.name}" does not match its declared size.`,
    );
  }
}

function parsePositiveInteger(value: string | undefined, fallback: number): number {
  const trimmed = value?.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return fallback;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function assertAggregateAdmission(
  files: readonly FileTransferMetadata[],
  limit: number,
  code: "comparison-too-large" | "zip-too-large",
  label: string,
): void {
  files.reduce((total, file) => {
    assertFileAdmission(file, limit);
    if (file.size > limit - total) {
      throw new FileTransferPolicyError(code, `${label} exceeds the ${limit}-byte total limit.`);
    }
    return total + file.size;
  }, 0);
}
